import React from "react";
import {
  ArrowRight,
  Sparkles,
  MessageCircle,
  Mail,
  Phone,
  Globe,
  Instagram,
  MessageSquare,
} from "lucide-react";
import ChatDemo from "./ChatDemo";
import MiniFlow from "./MiniFlow";

export default function Hero({ onDemoClick }) {
  return (
    <section
      data-testid="hero-section"
      className="relative pt-36 md:pt-44 pb-20 md:pb-28 overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-40 -left-20 w-[560px] h-[560px] glow-purple opacity-60" />
      <div className="pointer-events-none absolute top-20 right-0 w-[520px] h-[520px] glow-teal opacity-40" />
      <div className="pointer-events-none absolute inset-0 grid-bg" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="grid lg:grid-cols-[1.05fr_1.1fr] gap-12 lg:gap-16 items-start">
          {/* LEFT: copy */}
          <div className="fade-up">
            <div
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.02] backdrop-blur-sm mb-7"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#3DDFB3] soft-pulse" />
              <span className="text-[11px] font-mono tracking-[0.18em] text-white/70 uppercase">
                Agentic · Customer Interaction Platform
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-serif text-[52px] leading-[0.98] md:text-[68px] lg:text-[80px] tracking-tight text-white"
            >
              Elevate every
              <br />
              <span className="italic text-white/95">customer</span>{" "}
              interaction
              <span className="text-[#7C5CFF]">.</span>
            </h1>

            <p
              data-testid="hero-subtitle"
              className="mt-7 max-w-xl text-[16.5px] md:text-[17.5px] leading-relaxed text-white/65"
            >
              AI CRM is an{" "}
              <span className="text-white/90">Agentic AI-native</span> Customer
              Interaction Platform that automates customer engagement across
              every channel — WhatsApp, Calls, Web, Instagram, Email,
              Messenger.{" "}
              <span className="text-white/90">Everything.</span>
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <button
                data-testid="hero-primary-cta"
                onClick={onDemoClick}
                className="btn-primary text-white text-[15px] font-medium pl-6 pr-5 py-3 rounded-full inline-flex items-center gap-2"
              >
                Book a demo
                <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="#demo"
                data-testid="hero-secondary-cta"
                className="btn-ghost text-white text-[15px] font-medium px-6 py-3 rounded-full inline-flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4 text-[#3DDFB3]" />
                Watch product tour
              </a>
            </div>

            {/* Channel badges */}
            <div className="mt-12 flex flex-wrap items-center gap-x-5 gap-y-3">
              <span className="text-[11px] font-mono tracking-[0.18em] text-white/40 uppercase">
                Every channel
              </span>
              {[
                { icon: MessageCircle, label: "WhatsApp" },
                { icon: Phone, label: "Calls" },
                { icon: Globe, label: "Web" },
                { icon: Instagram, label: "Instagram" },
                { icon: Mail, label: "Email" },
                { icon: MessageSquare, label: "Messenger" },
              ].map((c) => (
                <div
                  key={c.label}
                  className="flex items-center gap-1.5 text-white/60 text-[12.5px]"
                  data-testid={`hero-channel-${c.label.toLowerCase().replace(" ", "-")}`}
                >
                  <c.icon className="w-3.5 h-3.5" />
                  {c.label}
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: stacked product visual — chat + flow */}
          <div
            className="relative fade-up space-y-5"
            style={{ animationDelay: "0.15s" }}
            data-testid="hero-product-visual"
          >
            <ChatDemo />

            {/* Connector note between chat and flow */}
            <div className="flex items-center gap-3 px-2">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">
                powered by
              </span>
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            </div>

            <MiniFlow />
          </div>
        </div>

        {/* Stats row */}
        <div
          data-testid="hero-stats-row"
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/[0.06] border border-white/[0.06] rounded-2xl overflow-hidden"
        >
          {[
            { k: "87%", l: "Tickets auto-resolved" },
            { k: "3.2s", l: "Avg. first response" },
            { k: "24/7", l: "Always-on coverage" },
            { k: "+41%", l: "CSAT uplift" },
          ].map((s, i) => (
            <div
              key={i}
              className="bg-[#0a0a10] px-6 py-7"
              data-testid={`hero-stat-${i}`}
            >
              <div className="font-serif text-4xl md:text-5xl text-white">
                {s.k}
              </div>
              <div className="mt-2 text-[12px] font-mono tracking-[0.12em] text-white/45 uppercase">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
